// The functional Summary screen (task 7.2; UI.md §6; design D-sum-1..4). Composes
// the match-end winner banner, the population timeline (complete/partial
// completeness contract), the locked three-tile stat row (peak bees / nectar
// gathered / prompts sent — with the mandated suppression rules), the "🏅 Best
// rewrite" highlight (+ its omitted state), and the two lobby CTAs. It reads the
// store only (`matchResult`/`manifest`/`matchTimeline`/`promptCycleCount`/
// `provenanceLog`/`colonyMeta`/`colonyId`) — no `Connection`, no dispatch — the
// same "reads only" contract `design.md` locks for this screen.
//
// The CTAs run over an injectable navigation seam (`onNavigate`, the `Match.tsx`
// `MatchProps.onNavigate` precedent): optional so the still-unwired production
// route (before task group 8's shell wiring) keeps compiling; the inert default
// (undefined) simply navigates nowhere rather than crashing.

import type { CSSProperties } from 'react';
import { useClientStore } from '../../state/store';
import { bestRewrite, summaryStats, timelineSeries } from '../match/summary-model';
import type { TimelineColonySeries } from '../match/summary-model';
import { colonyLabel, colonyPalette } from '../match/colony-visuals';
import { formatProvenanceSummary } from '../colony/version-model';
import { Button } from '../components/core/Button';

export interface SummaryProps {
  /** Injectable screen-navigation seam (design "Summary call-to-action
   * buttons"): called with the target route when a CTA is activated. Optional;
   * the inert default (`undefined`) navigates nowhere — the still-unwired
   * production route (task group 8) keeps compiling and never throws. */
  onNavigate?: (path: string) => void;
  primaryAction?: { label: string; path: string; external?: boolean };
}

// The season-band split fractions (DESIGN §14: 40% / 35% / 25%) now live on the shared
// `TimeSeriesChart` (`match-hud` **D-5**), which draws this screen's chart *and* the Match
// screen's statistics graph — the whole reason for the extraction is that two hand-rolled
// copies of the same SVG could disagree about where autumn starts.
import { TimeSeriesChart, CHART_SEASON_BANDS as SEASON_BANDS } from '../components/chart/TimeSeriesChart';

function formatDuration(tick: number, tickRateHz: number): string {
  const totalSeconds = Math.max(0, Math.round(tick / tickRateHz));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** The season name whose band contains `fraction` (0-1 of the chart's tick
 * range) — used only to caption the partial timeline's unobserved-range badge
 * ("joined at autumn"), never to source an actual per-tick `MatchStateObs.season`
 * (the accreted `matchTimeline` sample doesn't carry one; see `TimelineSample`). */
function seasonNameAtFraction(fraction: number): string {
  const band = SEASON_BANDS.find((b) => fraction <= b.to) ?? SEASON_BANDS[SEASON_BANDS.length - 1];
  return band.label;
}

/** The colony ids to draw at full emphasis (design "population timeline...
 * top colonies + own colony highlighted"): the top `topN` colonies by peak
 * observed `livingCount`, plus the own colony (always included, whatever its
 * rank) — the rest of the colonies still draw, dimmed, so the chart never hides
 * a colony's line outright. */
function highlightedColonyIds(
  series: readonly TimelineColonySeries[],
  ownColonyId: number | null,
  topN = 3,
): Set<number> {
  const peakOf = (s: TimelineColonySeries) =>
    s.points.reduce((max, p) => Math.max(max, p.livingCount), 0);
  const ranked = [...series].sort((a, b) => peakOf(b) - peakOf(a));
  const ids = new Set(ranked.slice(0, topN).map((s) => s.colonyId));
  if (ownColonyId !== null) ids.add(ownColonyId);
  return ids;
}

const CHART_WIDTH = 340;
const CHART_HEIGHT = 150;

function typeLabel(
  typeId: number,
  version: number,
  colonyMeta: ReturnType<typeof useClientStore.getState>['colonyMeta'],
): string {
  const type = colonyMeta?.types.find((t) => t.typeId === typeId);
  const name = type?.name ?? `Type ${typeId}`;
  return `${name} v${version}`;
}

const cardStyle: CSSProperties = {
  background: 'var(--surface-card)',
  border: '1px solid var(--line)',
  borderRadius: 16,
  boxShadow: 'var(--shadow-card)',
  padding: 14,
};

export function Summary({ onNavigate, primaryAction }: SummaryProps) {
  const matchResult = useClientStore((s) => s.matchResult);
  const manifest = useClientStore((s) => s.manifest);
  const colonyId = useClientStore((s) => s.colonyId);
  const matchTimeline = useClientStore((s) => s.matchTimeline);
  const promptCycleCount = useClientStore((s) => s.promptCycleCount);
  const provenanceLog = useClientStore((s) => s.provenanceLog);
  const colonyMeta = useClientStore((s) => s.colonyMeta);

  if (matchResult === null || manifest === null) {
    // No `result` message observed yet this session (design.md "reads the store
    // only" — the screen invents no data): a graceful placeholder, never a crash.
    // `manifest === null` is unreachable once `matchResult` is set (`Welcome` always
    // precedes a `result` on the wire) but is checked explicitly rather than asserted, so
    // `tickRateHz` below is **wire-required** (client-tick-rate-provenance E4) with no
    // defaulted argument — a genuine gap renders the same honest placeholder rather than
    // inventing a rate.
    return (
      <div data-testid="summary-screen" style={{ padding: 24 }}>
        <div data-testid="summary-waiting">Waiting for the match result…</div>
      </div>
    );
  }

  const tickRateHz = manifest.tickRateHz;
  const winner = [...matchResult.placements].sort((a, b) => a.rank - b.rank)[0];
  const outlivedCount = matchResult.placements.filter((p) => p.rank > winner.rank).length;
  const isOwnWin = colonyId !== null && winner.colonyId === colonyId;
  const winnerLabel = colonyLabel(winner.colonyId, colonyId);
  const durationLabel = formatDuration(matchResult.tick, tickRateHz);

  const stats = summaryStats(matchTimeline, promptCycleCount, colonyId);
  const rewrite = bestRewrite(matchTimeline, provenanceLog, colonyId);
  const shaped = timelineSeries(matchTimeline);
  const totalTicks = Math.max(matchResult.tick, ...shaped.series.flatMap((s) => s.points.map((p) => p.tick)), 1);
  const highlighted = highlightedColonyIds(shaped.series, colonyId);
  const unobservedFraction =
    matchTimeline.startTick !== null ? Math.min(1, matchTimeline.startTick / totalTicks) : 0;

  const maxLivingCount = Math.max(
    1,
    ...shaped.series.flatMap((s) => s.points.map((p) => p.livingCount)),
  );
  // One `ChartSeries` per colony — the same lines this screen always drew, now expressed as data
  // the shared chart renders rather than as SVG assembled here.
  const chartSeries = shaped.series.map((s) => {
    const isHighlighted = highlighted.has(s.colonyId);
    return {
      id: String(s.colonyId),
      testId: `timeline-line-${s.colonyId}`,
      label: colonyLabel(s.colonyId, colonyId),
      colour: colonyPalette(s.colonyId),
      kind: 'line' as const,
      points: s.points.map((p) => ({ tick: p.tick, value: p.livingCount })),
      strokeWidth: isHighlighted ? 2.5 : 1,
      opacity: isHighlighted ? 1 : 0.35,
    };
  });

  return (
    <div
      data-testid="summary-screen"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: '26px 18px 18px',
        maxWidth: 420,
        margin: '0 auto',
      }}
    >
      {/* Winner banner (UI.md §6). */}
      <div data-testid="winner-banner" style={{ textAlign: 'center', padding: '14px 0 4px' }}>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 28,
            color: 'var(--ink-1)',
          }}
        >
          {winnerLabel} wins
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 2 }}>
          {isOwnWin ? 'Your colony outlived ' : 'Outlived '}
          {outlivedCount} others · {durationLabel}
        </div>
      </div>

      {/* Population timeline (design D-sum-1). */}
      <div style={cardStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 8,
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontWeight: 800,
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '.08em',
              color: 'var(--text-muted)',
            }}
          >
            Colony populations
          </span>
          {matchTimeline.complete ? (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>complete</span>
          ) : (
            <span
              data-testid="timeline-partial-badge"
              style={{
                padding: '3px 8px',
                borderRadius: 8,
                background: 'var(--danger-soft)',
                color: 'var(--danger)',
                fontWeight: 800,
                fontSize: 9,
                textTransform: 'uppercase',
                letterSpacing: '.07em',
              }}
            >
              Partial · joined at {seasonNameAtFraction(unobservedFraction)}
            </span>
          )}
        </div>
        <TimeSeriesChart
          series={chartSeries}
          totalTicks={totalTicks}
          maxValue={maxLivingCount}
          unobservedFraction={matchTimeline.complete ? 0 : unobservedFraction}
          width={CHART_WIDTH}
          height={CHART_HEIGHT}
          ariaLabel="Colony populations over the match"
        />
        <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
          {shaped.series
            .filter((s) => highlighted.has(s.colonyId))
            .map((s) => (
              <span
                key={s.colonyId}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--ink-2)', fontWeight: 700 }}
              >
                <i
                  style={{
                    width: 10,
                    height: 3,
                    borderRadius: 2,
                    display: 'inline-block',
                    background: colonyPalette(s.colonyId),
                  }}
                />
                {colonyLabel(s.colonyId, colonyId)}
              </span>
            ))}
        </div>
      </div>

      {/* Locked three-tile stat row (design D-sum-2). */}
      <div style={{ display: 'flex', gap: 10 }}>
        <StatTile testId="stat-peak-bees" value={stats.peakBees} label="peak bees" why={stats.peakBees === null ? 'partial · suppressed' : undefined} />
        <StatTile testId="stat-nectar-gathered" value={stats.nectarGathered} label="nectar gathered" why="no cumulative stat on wire · D-076" />
        <StatTile testId="stat-prompts-sent" value={stats.promptsSent} label="prompts sent" why={stats.promptsSent === null ? 'partial · suppressed' : undefined} />
      </div>

      {/* Best-rewrite highlight (design D-sum-3). */}
      {rewrite !== null ? (
        <div
          data-testid="best-rewrite"
          style={{
            background: 'var(--surface-sunken)',
            borderRadius: 12,
            padding: '12px 14px',
            fontSize: 13,
            color: 'var(--ink-2)',
            lineHeight: 1.5,
          }}
        >
          🏅 <b>Best rewrite:</b>{' '}
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
            {typeLabel(rewrite.typeId, rewrite.version, colonyMeta)}
          </code>{' '}
          — {formatProvenanceSummary(rewrite.provenance, matchResult.tick, tickRateHz)}.{' '}
          <span style={{ color: 'var(--ink-3)' }}>
            Quoted from version provenance; the player's prompt text is not echoed to the client (D-078).
          </span>
        </div>
      ) : (
        <div
          data-testid="best-rewrite-omitted"
          style={{
            background: 'var(--surface-sunken)',
            borderRadius: 12,
            padding: '12px 14px',
            fontSize: 13,
            color: 'var(--ink-3)',
            lineHeight: 1.5,
          }}
        >
          🏅 Best rewrite unavailable — the observed population series is incomplete, so no rewrite can be
          honestly credited.
        </div>
      )}

      <div style={{ flex: 1 }} />

      {/* CTAs (design "Summary call-to-action buttons"): both route to the lobby
          through the injected navigation seam — the shipped surface has no
          separate "same lobby" vs "generic back" route to distinguish them. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {primaryAction?.external === true ? (
          <a href={primaryAction.path} style={PRIMARY_LINK}>{primaryAction.label}</a>
        ) : (
          <Button variant="primary" size="lg" onClick={() => onNavigate?.(primaryAction?.path ?? '/')}>
            {primaryAction?.label ?? 'Play again with this lobby'}
          </Button>
        )}
        <Button variant="secondary" size="lg" onClick={() => onNavigate?.('/')}>
          Back to lobby
        </Button>
      </div>
    </div>
  );
}

const PRIMARY_LINK: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
  minHeight: 'var(--hit-target)', padding: '4px 28px', borderRadius: 'var(--r-pill)',
  background: 'var(--accent-strong)', color: 'var(--surface-card)',
  border: '1px solid var(--accent-strong)', fontFamily: 'var(--font-display)',
  fontWeight: 500, fontSize: 17, textDecoration: 'none',
};

interface StatTileProps {
  testId: string;
  value: number | null;
  label: string;
  why?: string;
}

function StatTile({ testId, value, label, why }: StatTileProps) {
  return (
    <div
      data-testid={testId}
      style={{
        flex: 1,
        background: 'var(--surface-card)',
        border: '1px solid var(--line)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-card)',
        padding: 10,
        textAlign: 'center',
      }}
    >
      <b
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: 20,
          color: value === null ? 'var(--ink-3)' : 'var(--ink-1)',
          display: 'block',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value === null ? '—' : value}
      </b>
      <span
        style={{
          fontSize: 10,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '.08em',
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      {why !== undefined && (
        <span style={{ display: 'block', marginTop: 3, fontSize: 8, fontWeight: 700, color: 'var(--ink-3)' }}>
          {why}
        </span>
      )}
    </div>
  );
}
